import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PageWrapper, Card, Button } from "../components";
import { 
  Mic, Square, Play, Pause, Upload, Check, 
  ArrowLeft, AlertCircle, RotateCcw, ChevronDown
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sample script for users to read - helps create better voice models
const SAMPLE_SCRIPT = `Today I reclaim my voice.
I speak with clarity and confidence.
My words are calm, grounded, and natural.
I trust the journey of my life.
Every day I become more aligned with who I truly am.
I am at peace with this present moment.`;

export const VoiceRecordPage = () => {
  const navigate = useNavigate();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [step, setStep] = useState("intro"); // intro, recording, preview, uploading, success, error
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [voiceName, setVoiceName] = useState("My Voice");
  const [uploadProgress, setUploadProgress] = useState("");
  const [showUploadOption, setShowUploadOption] = useState(false);

  const getToken = () => localStorage.getItem("reclaim_token");

  // Recording timer
  useEffect(() => {
    let interval;
    if (step === "recording") {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      setError(null);
      
      // Check if MediaRecorder is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Your browser doesn't support audio recording. Please try uploading a file instead.");
        setShowUploadOption(true);
        return;
      }

      // Check if MediaRecorder is available
      if (typeof MediaRecorder === 'undefined') {
        setError("Audio recording is not supported on this browser. Please try uploading a file instead.");
        setShowUploadOption(true);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      streamRef.current = stream;
      
      // Determine supported mimeType
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
          mimeType = 'audio/wav';
        } else {
          // Fallback - let browser decide
          mimeType = '';
        }
      }
      
      const mediaRecorder = mimeType 
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType;
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setStep("preview");
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000);
      setStep("recording");
      setRecordingTime(0);
      toast.success("Recording started!");
    } catch (err) {
      console.error("Recording error:", err);
      let errorMsg = "Could not access microphone. Please try uploading a file instead.";
      
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errorMsg = "Please allow microphone access to record your voice. You can also upload an audio file instead.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errorMsg = "No microphone found. Please connect a microphone or upload a file instead.";
      } else if (err.name === "NotSupportedError") {
        errorMsg = "Audio recording is not supported on this device. Please upload a file instead.";
      }
      
      setError(errorMsg);
      toast.error("Recording failed - try uploading a file instead");
      setShowUploadOption(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = [".mp3", ".wav", ".m4a", ".mp4", ".webm"];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!hasValidExtension) {
      setError("Please upload an MP3, WAV, or M4A audio file.");
      return;
    }

    setAudioBlob(file);
    setAudioUrl(URL.createObjectURL(file));
    setStep("preview");
    setError(null);
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleReRecord = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setStep("intro");
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const handleSubmit = async () => {
    if (!audioBlob) return;

    setStep("uploading");
    setUploadProgress("Creating your voice...");
    setError(null);

    const authToken = getToken();
    const formData = new FormData();
    
    let extension = ".webm";
    if (audioBlob.type?.includes("mp3") || audioBlob.type?.includes("mpeg")) extension = ".mp3";
    else if (audioBlob.type?.includes("wav")) extension = ".wav";
    else if (audioBlob.type?.includes("m4a") || audioBlob.type?.includes("mp4")) extension = ".m4a";
    
    const fileName = `voice_recording${extension}`;
    formData.append("voice_name", voiceName);
    formData.append("audio_file", audioBlob, fileName);

    try {
      const res = await fetch(`${API_URL}/api/voice-studio/record-voice`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Failed to create voice");
      }

      setStep("success");
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message);
      setStep("preview");
    }
  };

  return (
    <PageWrapper showNav={false}>
      <div className="min-h-screen bg-gradient-to-b from-[#FAFAF7] to-[#F5F4F0]">
        {/* Hidden elements */}
        {audioUrl && (
          <audio 
            ref={audioRef} 
            src={audioUrl} 
            onEnded={() => setIsPlaying(false)}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Header */}
        <div className="bg-gradient-to-b from-[#2D4A2A] to-[#3D5A34] text-white px-6 pt-6 pb-10">
          <div className="max-w-lg mx-auto">
            <button
              onClick={() => navigate("/voice-studio")}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            <h1 className="font-heading text-2xl mb-2">Record My Voice</h1>
            <p className="text-white/70 text-sm">
              Create your personal voice in under a minute
            </p>
          </div>
        </div>

        <div className="px-6 py-6 max-w-lg mx-auto -mt-4">
          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3"
              >
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* INTRO STATE - Main entry point */}
          {step === "intro" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Main Record Button */}
              <Card className="p-8 text-center bg-white shadow-lg">
                <motion.button
                  onClick={startRecording}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-32 h-32 bg-gradient-to-br from-[#4A6741] to-[#3D5A34] rounded-full flex items-center justify-center mx-auto shadow-xl shadow-[#4A6741]/30 mb-6"
                >
                  <Mic size={48} className="text-white" />
                </motion.button>
                
                <h2 className="font-heading text-2xl text-[#1F2937] mb-2">
                  Record My Voice
                </h2>
                <p className="text-[#6B7280] text-sm mb-6">
                  Tap the button above to start recording
                </p>
                
                <Button onClick={startRecording} className="w-full py-4 text-lg" data-testid="start-recording-btn">
                  <Mic size={22} />
                  Start Recording
                </Button>
              </Card>

              {/* Sample Script Card */}
              <Card className="p-6 bg-[#F8F7F5] border border-[#E5E7EB]">
                <h3 className="font-heading text-lg text-[#1F2937] mb-3">
                  What to say
                </h3>
                <p className="text-sm text-[#6B7280] mb-4">
                  Read this script slowly and naturally (30-60 seconds):
                </p>
                <div className="bg-white rounded-xl p-4 border border-[#E5E7EB]">
                  <p className="text-[#1F2937] leading-relaxed italic whitespace-pre-line">
                    "{SAMPLE_SCRIPT}"
                  </p>
                </div>
              </Card>

              {/* Upload Option - Secondary */}
              <div className="pt-2">
                <button
                  onClick={() => setShowUploadOption(!showUploadOption)}
                  className="flex items-center gap-2 text-sm text-[#6B7280] mx-auto hover:text-[#4A6741] transition-colors"
                >
                  <span>Already have an audio file?</span>
                  <ChevronDown size={16} className={`transition-transform ${showUploadOption ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {showUploadOption && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4"
                    >
                      <Button 
                        variant="outline" 
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload size={18} />
                        Upload Audio File (MP3, WAV, M4A)
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* RECORDING STATE */}
          {step === "recording" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              <Card className="p-8 text-center bg-white shadow-lg">
                {/* Pulsing record indicator */}
                <div className="w-28 h-28 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-red-500/30 animate-record-pulse">
                  <Mic size={44} className="text-white" />
                </div>

                {/* Timer */}
                <div className="mb-4">
                  <p className="text-5xl font-mono font-bold text-[#1F2937]">
                    {formatTime(recordingTime)}
                  </p>
                  <p className="text-[#6B7280] text-sm mt-2">
                    Recording...
                  </p>
                </div>

                {/* Audio visualization */}
                <div className="flex items-center justify-center gap-1.5 mb-6 h-12">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="w-2 h-10 bg-red-400 rounded-full animate-audio-bar"
                      style={{ animationDelay: `${i * 0.08}s` }}
                    />
                  ))}
                </div>

                {/* Progress hint */}
                <p className="text-sm text-[#6B7280] mb-6">
                  {recordingTime < 30 
                    ? `Keep going! ${30 - recordingTime} seconds to go...` 
                    : "Great! You can stop anytime now."}
                </p>

                <Button onClick={stopRecording} className="w-full py-4 bg-red-500 hover:bg-red-600">
                  <Square size={20} />
                  Stop Recording
                </Button>
              </Card>

              {/* Script reminder */}
              <Card className="p-4 bg-[#F8F7F5]/80">
                <p className="text-sm text-[#6B7280] text-center italic">
                  "{SAMPLE_SCRIPT.split('\n')[Math.min(Math.floor(recordingTime / 10), 5)]}"
                </p>
              </Card>
            </motion.div>
          )}

          {/* PREVIEW STATE */}
          {step === "preview" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="p-8 bg-white shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-[#4A6741]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={36} className="text-[#4A6741]" />
                  </div>
                  <h2 className="font-heading text-xl text-[#1F2937] mb-2">
                    Recording Complete
                  </h2>
                  <p className="text-[#6B7280] text-sm">
                    Listen to your recording before creating your voice
                  </p>
                </div>

                {/* Playback button */}
                <div className="flex items-center justify-center mb-6">
                  <button
                    onClick={togglePlayback}
                    className="w-20 h-20 bg-[#4A6741] rounded-full flex items-center justify-center text-white shadow-lg hover:bg-[#3D5A34] transition-colors"
                  >
                    {isPlaying ? <Pause size={32} /> : <Play size={32} className="ml-1" />}
                  </button>
                </div>

                {/* Voice Name Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1F2937] mb-2">
                    Name your voice
                  </label>
                  <input
                    type="text"
                    value={voiceName}
                    onChange={(e) => setVoiceName(e.target.value)}
                    placeholder="My Voice"
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A6741]/30 text-center text-lg"
                  />
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button onClick={handleSubmit} className="w-full py-4 text-lg">
                    <Check size={22} />
                    Use This Recording
                  </Button>
                  <Button variant="outline" onClick={handleReRecord} className="w-full">
                    <RotateCcw size={18} />
                    Record Again
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* UPLOADING STATE */}
          {step === "uploading" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-8"
            >
              <Card className="p-8 text-center bg-white shadow-lg">
                <div className="w-20 h-20 border-4 border-[#4A6741]/20 border-t-[#4A6741] rounded-full animate-spin mx-auto mb-6" />
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">
                  Creating Your Voice
                </h2>
                <p className="text-[#6B7280] text-sm mb-4">
                  {uploadProgress}
                </p>
                <p className="text-xs text-[#9CA3AF]">
                  This takes about 30 seconds...
                </p>
              </Card>
            </motion.div>
          )}

          {/* SUCCESS STATE */}
          {step === "success" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-4"
            >
              <Card className="p-8 text-center bg-white shadow-lg">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", damping: 15 }}
                  className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Check size={48} className="text-green-600" />
                </motion.div>
                <h2 className="font-heading text-2xl text-[#1F2937] mb-2">
                  Your Voice Is Ready!
                </h2>
                <p className="text-[#6B7280] mb-8">
                  You can now create personalized installations using your own voice.
                </p>
                <Button onClick={() => navigate("/voice-studio/create")} className="w-full py-4 text-lg">
                  Create My First Installation
                </Button>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default VoiceRecordPage;
